import React, { useEffect, useState, useRef, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import draftToHtml from 'draftjs-to-html';
import translations from 'utils/translations';
import {
  maxInvitations,
  maxChatInvitations,
  backHomeLink,
} from 'utils/constants';
import { breakoutRoomInviteModel } from 'entities/breakoutRoomInviteModel';
import { breakoutRoomModel } from 'entities/breakoutRoomModel';
import { userModel } from 'entities/userModel';

import { LoaderContext } from 'context/LoaderContextProvider';
import { AuthContext } from 'context/AuthContextProvider';
import { theme } from 'theme';
import { purposesEnum, languagesOptions } from 'utils/constants';

import { useTheme, styled } from '@material-ui/core/styles';
import { Grid, List, ListItem, useMediaQuery } from '@material-ui/core';

import HighlightOffIcon from '@material-ui/icons/HighlightOff';

import {
  SdmRichText,
  SdmTypography,
  SdmSpacer,
  SdmTextButton,
  SdmSelect,
} from 'components';

import { CustomFormTextField } from './BreakoutRoomForm';
import { useEmailHelper } from 'services/emailSender';

const deafultMessages = {
  en: {
    br: 'We invite you to join the next Breakout Room',
    chat: 'We invite you to join the next Live Chat',
  },
  it: {
    br: 'Ti invitiamo a partecipare alla prossima Breakout Room',
    chat: 'Ti invitiamo a partecipare alla prossima Live Chat',
  },
};

const submitKeys = ['Enter', 'Tab'];

const HorizontalList = styled(List)({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  height: '100%',
  flexWrap: 'wrap',
});

const EmailItem = styled(ListItem)({
  color: theme.colors.primary.red,
  width: 'inherit',
});

function BreakoutRoomInvitationsHandler({ purpose, locale }) {
  const history = useHistory();
  const intl = useIntl();
  const i18n = translations.handleInvitations(intl)[purpose];
  const i18nLang = translations.handleInvitations(intl).language;

  const { sendEmail, emailEnums } = useEmailHelper();

  const [emailList, setEmailList] = useState([]);
  const [emailText, setEmailText] = useState();
  const [currentInvited, setCurrentInvited] = useState([]);
  const [invitationSelected, setInvitationSelected] = useState([]);
  const [brSelected, setBrSelected] = useState([]);
  const [communicationLang, setCommunicationLang] = useState();
  const [messageDefaultValue, setMessageDefaultValue] = useState();
  const [generatedLink, setGeneratedLink] = useState('');

  const [error, setError] = useState(null);
  const { setLoaderActive } = useContext(LoaderContext);
  const { user } = useContext(AuthContext);

  const mediaTheme = useTheme();
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));
  const isXsDown = useMediaQuery(mediaTheme.breakpoints.down('xs'));

  //Refs
  const languageSelectRef = useRef();
  const input = useRef(null);

  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  useEffect(() => {
    const state = history.location.state;
    if (state?.breakoutRoom.id) {
      const br = state.breakoutRoom;
      if (purpose === purposesEnum.REMOVE || purpose === purposesEnum.DELETE) {
        if (state.invitationList) {
          setEmailList(state.invitationList.map((i) => i.user.email));
          setInvitationSelected(state.invitationList.map((i) => i.id));
          setBrSelected(br);
          history.replace(history.location.pathname, {
            breakoutRoom: br,
          });
        } else {
          handleBack();
        }
      } else {
        fetchInvitations(state?.breakoutRoom.id);
      }
    } else {
      handleBack();
    }
  }, []);

  useEffect(() => {
    if (locale) {
      setCommunicationLang(getLangOptions().find((o) => o.value === locale));
    }
  }, [locale]);

  useEffect(() => {
    if (communicationLang) setMessageDefaultValue(getDefaultMessage());
  }, [communicationLang]);

  const fetchInvitations = async (breakoutRoomId) => {
    showLoader();
    const response =
      await breakoutRoomInviteModel.gql.listBreakoutRoomInvitations(
        breakoutRoomId
      );
    setCurrentInvited(response.invitations.items.map((i) => i.user.email));
    hideLoader();
  };

  const handleBack = () => {
    history.push('/breakoutRoom', history.location.state);
  };

  const isChatType = (br) => {
    return br?.type === breakoutRoomModel.enums.types.CHAT;
  };

  const getDefaultMessage = () => {
    return (
      communicationLang &&
      deafultMessages[communicationLang.value][
        isChatType(history.location.state.breakoutRoom) ? 'chat' : 'br'
      ]
    );
  };

  const onSubmit = async () => {
    showLoader();
    const breakoutRoomId = history.location.state.breakoutRoom.id;
    const users = await userModel.gql.getFiltered(emailList);
    const emailAsHtml = emailText
      ? draftToHtml(emailText)
      : `<p>${getDefaultMessage()}</p>`;
    //const existUser = list.filter((email) => )
    const inputUserList = [];
    const inputBreakoutRoomInvitationList = [];
    users.map((user) => {
      //l'utente esiste, procedo con l'invito
      const input = {
        breakoutRoomInviteBreakoutRoomId: breakoutRoomId,
        breakoutRoomInviteUserId: user.id,
        status: breakoutRoomInviteModel.enums.status.PENDING,
      };
      inputBreakoutRoomInvitationList.push(input);
    });
    const existEmails = users.map((user) => user.email);
    const newusers = emailList.filter((email) => !existEmails.includes(email));
    newusers.map((email) => {
      const input = {
        email: email,
      };
      inputUserList.push(input);
    });

    if (inputUserList.length > 0) {
      const justCreatedUsers = await userModel.gql.createMultiple(
        inputUserList
      );
      //eseguo la creazione degli utenti non censiti
      //const justCreatedUsers = await Promise.all(createUserPromises);
      Object.values(justCreatedUsers).map((user) => {
        //ad ogni utente posso associare l'invito
        const input = {
          breakoutRoomInviteBreakoutRoomId: breakoutRoomId,
          breakoutRoomInviteUserId: user.id,
          status: breakoutRoomInviteModel.enums.status.PENDING,
        };
        //voglio ricevere anche i dati dell'utente alla fine della create
        inputBreakoutRoomInvitationList.push(input);
      });
    }

    if (inputBreakoutRoomInvitationList.length > 0) {
      //procedo con la creazione complessiva degli inviti
      await breakoutRoomInviteModel.gql.createMultiple(
        inputBreakoutRoomInvitationList
      );
      await sendEmail(emailEnums.INVITE_NOTIIFCATION, {
        brandMessage: emailAsHtml,
        brandName: user.name,
        breakoutRoom: history.location.state.breakoutRoom,
        targets: emailList,
        langcode: communicationLang.value,
        is_live_chat: isChatType(history.location.state.breakoutRoom),
      });
    }
    hideLoader();
    history.push('/breakoutRoom', history.location.state);
  };

  const onDelete = async () => {
    showLoader();
    const emailAsHtml = draftToHtml(emailText);
    if (purpose === purposesEnum.DELETE) {
      await breakoutRoomModel.gql.remove(brSelected.id);
    } else {
      const updateInputList = [];
      invitationSelected.map((invitationId) => {
        const input = {
          id: invitationId,
          status: breakoutRoomInviteModel.enums.status.CANCELED,
        };
        updateInputList.push(input);
      });
      await breakoutRoomInviteModel.gql.updateMultiple(updateInputList);
    }
    if (emailList.length > 0) {
      await sendEmail(emailEnums.DELETE_BR_NOTIFICATION, {
        brandMessage: emailAsHtml,
        brandName: user.name,
        breakoutRoom: history.location.state.breakoutRoom,
        targets: emailList,
        langcode: communicationLang.value,
        is_live_chat: isChatType(history.location.state.breakoutRoom),
      });
    }
    hideLoader();
    if (purpose === purposesEnum.DELETE) {
      history.push('/', null);
    } else {
      history.push('/breakoutRoom', history.location.state);
    }
  };

  const removeItem = (idx) => {
    emailList.splice(idx, 1);
    setEmailList([...emailList]);
  };

  const onGenerateLink = () => {
    const breakoutRoomId = history.location.state.breakoutRoom.id;
    const link = `${backHomeLink}/${locale}/breakout?br-id=${breakoutRoomId}`;
    setGeneratedLink(link);
  };

  const validate = (email, list) => {
    const _br = history?.location?.state?.breakoutRoom;
    const _isChatType = isChatType(_br);
    const messages = i18n.errors;
    if (
      (!_isChatType && list.length + currentInvited.length >= maxInvitations) ||
      list.length + currentInvited.length >= maxChatInvitations
    )
      throw messages.tooMuchPax;
    const emailRegex =
      /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const found = email.match(emailRegex);
    if (!found) throw `${email} ${messages.invalidEmail}`;
    if (list.includes(email)) throw `${email} ${messages.doubleEmail}`;
    if (currentInvited.includes(email))
      throw `${email} ${messages.invitedEmail}`;
  };

  const validateEmail = (email, list) => {
    validate(email, list);
    list.push(email);
    return list;
  };

  const tryComplete = (e) => {
    if (submitKeys.includes(e.code)) {
      const emails = e.target.value.toLowerCase().replace(/\s/g, '');
      const errors = [];
      const emailsCatched = [];
      let validatedEmail = [...emailList];
      emails.split(';').map((singleEmail) => {
        if (singleEmail) {
          try {
            validatedEmail = validateEmail(singleEmail, validatedEmail);
          } catch (err) {
            errors.push(err);
            emailsCatched.push(singleEmail);
          }
        }
      });
      if (errors.length) {
        setError([...new Set(errors)].join('; '));
        e.target.value = emailsCatched.join('; ');
      } else {
        e.target.value = '';
      }
      setEmailList([...validatedEmail]);
    }
    e.stopPropagation();
  };

  const getLangOptions = () => {
    return languagesOptions.map((s) => ({
      label: i18nLang.values[`${s}_${s.toUpperCase()}`],
      value: s,
    }));
  };

  const renderError = () => {
    return (
      <SdmTypography
        variant={'textBold'}
        style={{ color: `${theme.colors.primary.red}` }}
      >
        {error}
      </SdmTypography>
    );
  };

  return (
    <div style={{ width: '80%', margin: '0 auto', maxWidth: 1250 }}>
      <div style={{ margin: '50px auto 0', maxWidth: 736 }}>
        <Grid container>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <SdmTypography
                  textAlign={'center'}
                  variant={isXsDown ? 'h1' : 'pageTitle'}
                >
                  {i18n.title}
                </SdmTypography>
              </Grid>
              <SdmSpacer height={isXsDown ? 5 : 24} />
              <Grid item xs={12}>
                <SdmTypography
                  variant={isXsDown ? 'h2Regular' : 'h1Regular'}
                  margin={0}
                  textAlign={isXsDown ? 'center' : 'left'}
                  style={{ margin: '8px 0' }}
                >
                  {i18n.subtitle}
                </SdmTypography>
                <SdmTypography
                  variant={isXsDown ? 'h5Regular' : 'h4Regular'}
                  margin={0}
                  textAlign={isXsDown ? 'center' : 'left'}
                  style={{ margin: '8px 0' }}
                >
                  {emailList.length > 0 ||
                  (purpose && purpose !== purposesEnum.DELETE)
                    ? i18n.generalText
                    : i18n.emptyText}
                </SdmTypography>
              </Grid>
            </Grid>
          </Grid>
          {purpose && purpose === purposesEnum.INVITE && (
            <SdmSpacer height={24} />
          )}
          {purpose && purpose === purposesEnum.INVITE && (
            <Grid item xs={12}>
              <CustomFormTextField
                fullWidth
                ref={input}
                autoFocus
                type="email"
                label={i18n.emailListInput.label}
                onChange={() => {
                  setError(null);
                }}
                onKeyDown={tryComplete}
                onBlur={() => {
                  input.current.focus();
                }}
              />
              {renderError()}
            </Grid>
          )}
          <Grid item xs={12}>
            <HorizontalList>
              {emailList.map((item, idx) => {
                return (
                  <EmailItem key={item}>
                    {item}
                    {purpose && purpose === purposesEnum.INVITE && (
                      <HighlightOffIcon
                        fontSize={'small'}
                        onClick={() => {
                          removeItem(idx);
                        }}
                        style={{ marginLeft: 5, cursor: 'pointer' }}
                      />
                    )}
                  </EmailItem>
                );
              })}
            </HorizontalList>
          </Grid>
          {emailList.length > 0 ||
          (purpose && purpose !== purposesEnum.DELETE && communicationLang) ? (
            <>
              <SdmSpacer height={24} />
              <Grid item xs={12}>
                <SdmTypography variant={'h6Regular'} margin={0}>
                  {i18n.emailTextInput.label}
                </SdmTypography>
                <SdmRichText
                  defaultValue={
                    purpose === purposesEnum.INVITE
                      ? `<p>${messageDefaultValue}</p>`
                      : ''
                  }
                  onChange={(value) => setEmailText(value)}
                  id="description-required"
                  variant={'grey'}
                  formControlStyle={{
                    margin: 0,
                  }}
                />
              </Grid>
            </>
          ) : null}
          {isXsDown && <SdmSpacer height={20} />}
          {!isXsDown && <SdmSpacer height={20} />}

          <Grid item md={5} />
          {emailList.length > 0 ||
          (purpose && purpose !== purposesEnum.DELETE) ? (
            <Grid item md={7} xs={12}>
              <div
                style={{
                  marginLeft: 'auto',
                  marginRight: 4,
                  width: isSmDown ? '100%' : '90%',
                }}
              >
                <SdmSelect
                  options={getLangOptions()}
                  value={communicationLang}
                  labelKey={'label'}
                  label={i18nLang.label}
                  onSelect={(e) => setCommunicationLang(e)}
                  ref={languageSelectRef}
                />
              </div>
            </Grid>
          ) : null}
          {!isXsDown && <SdmSpacer height={20} />}
          {isXsDown && <SdmSpacer height={20} />}
          {purpose && purpose === purposesEnum.INVITE && (
            <Grid item xs={12}>
              <SdmTypography
                variant={isXsDown ? 'h5Regular' : 'h4Regular'}
                margin={0}
                textAlign={isXsDown ? 'center' : 'left'}
                style={{ margin: '8px 0' }}
              >
                {i18n.public.text}
              </SdmTypography>
              <SdmTextButton
                variant={'redFill'}
                text={i18n.public.link}
                textVariant={'h4Regular'}
                onClick={onGenerateLink}
                style={{
                  minWidth: isXsDown ? 110 : 192,
                  height: 46,
                }}
              />
              {generatedLink && (
                <SdmTypography
                  variant={'h4'}
                  margin={0}
                  style={{
                    color: theme.colors.primary.red,
                    margin: '15px 0',
                  }}
                >
                  {generatedLink}
                </SdmTypography>
              )}
              <SdmTypography
                variant={isXsDown ? 'h2Regular' : 'h1Regular'}
                margin={0}
                textAlign={isXsDown ? 'center' : 'left'}
                style={{ margin: '8px 0' }}
              >
                {i18n.public.warning}
              </SdmTypography>
              <SdmTypography
                variant={isXsDown ? 'h5Regular' : 'h4Regular'}
                margin={0}
                textAlign={isXsDown ? 'center' : 'left'}
                style={{ margin: '8px 0' }}
              >
                {i18n.public.warningText}
              </SdmTypography>
              {!isXsDown && <SdmSpacer height={20} />}
            </Grid>
          )}
          <Grid item md={5} />
          <Grid item md={7} xs={12}>
            <Grid
              container
              alignItems={'center'}
              justify={isXsDown ? 'space-between' : 'flex-end'}
            >
              <SdmTextButton
                variant={'red'}
                text={i18n.leftButton}
                textVariant={'h4Regular'}
                onClick={handleBack}
                style={{
                  minWidth: isXsDown ? 110 : 192,
                  height: 46,
                }}
              />
              <SdmTextButton
                variant={'redFill'}
                text={i18n.rightButton}
                textVariant={'h4Regular'}
                disabled={
                  emailList.length > 0 ||
                  (purpose && purpose !== purposesEnum.DELETE)
                    ? !!error || emailList.length === 0
                    : false
                }
                onClick={purpose === purposesEnum.INVITE ? onSubmit : onDelete}
                style={{
                  minWidth: isXsDown ? 110 : 192,
                  height: 46,
                }}
              />
            </Grid>
          </Grid>
          {isXsDown && <SdmSpacer height={20} />}
          {!isXsDown && <SdmSpacer height={100} />}
        </Grid>
      </div>
    </div>
  );
}

export default BreakoutRoomInvitationsHandler;
